import React, { useState } from 'react';
import { ArrowRight, BadgeCheck, Menu, X } from 'lucide-react';

import BookDemo from './components/BookDemo';
import Photo from './components/Photo';
import PhoneShot from './components/PhoneShot';
import { useCountUp, useRevealRoot, useScrolledPast } from './lib/motion';

import './styles/tokens.css';
import './styles/base.css';
import './styles/sections.css';

/* =============================================================================
 * StatMe — statme.us
 *
 * ACCURACY IS LOAD-BEARING ON THIS PAGE. The app was rejected twice by Apple
 * partly over metadata that described things the product did not do, and the
 * audience is 13 to 17 year olds and their parents. Every capability claimed
 * below was checked against the shipped app before it was written:
 *
 *   - every app image on this page is a REAL screenshot captured from the
 *     shipped build on the iOS Simulator (fixture data, believable numbers).
 *   - scouts message COACHES ONLY, never players or parents. Enforced in
 *     infra/amplify/data/_shared/scout-messaging.ts in both directions.
 *   - film is capped at five minutes per clip, signed CDN URLs, team only.
 *   - free: no ads, no in-app purchases, no ad identifier, no third-party
 *     analytics SDK. iPhone, United States, ages 13 and up.
 *
 * Things that DO NOT exist and must never appear here: highlight reels,
 * Android, a web app, PDF or link profile export, RSVP, AI coaching,
 * subscriptions.
 * ===========================================================================*/

const APP_STORE_URL =
  'https://apps.apple.com/us/app/statme-basketball-stats/id6784947109';

const NAV = [
  { id: 'tracking', label: 'Tracking' },
  { id: 'profile', label: 'Profile' },
  { id: 'scouts', label: 'Scouts' },
];

function Check() {
  return <BadgeCheck size={20} strokeWidth={2} aria-hidden="true" />;
}

/* -------------------------------------------------------------------------- */

function Nav({ onBookDemo }) {
  const stuck = useScrolledPast(60);
  const [open, setOpen] = useState(false);

  return (
    <header className={`nav${stuck || open ? ' is-stuck' : ''}`}>
      <div className="nav__inner">
        <a className="nav__brand" href="#top">
          <img className="nav__mark" src="/number1logo.png" alt="" />
          StatMe
        </a>

        <nav className="nav__links" aria-label="Sections">
          {NAV.map((item) => (
            <a key={item.id} className="nav__link" href={`#${item.id}`}>
              {item.label}
            </a>
          ))}
          <button className="btn btn--primary nav__cta" onClick={onBookDemo}>
            Book a demo
          </button>
        </nav>

        <button
          className="nav__burger"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls="nav-sheet"
          aria-label={open ? 'Close menu' : 'Open menu'}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open ? (
        <div className="nav__sheet" id="nav-sheet">
          {NAV.map((item) => (
            <a key={item.id} href={`#${item.id}`} onClick={() => setOpen(false)}>
              {item.label}
            </a>
          ))}
          <button
            className="btn btn--primary"
            style={{ marginTop: '1rem' }}
            onClick={() => {
              setOpen(false);
              onBookDemo();
            }}
          >
            Book a demo
          </button>
        </div>
      ) : null}
    </header>
  );
}

/* -------------------------------------------------------------------------- */

function Hero({ onBookDemo }) {
  const cueFaded = useScrolledPast(40);

  return (
    <section className="hero on-night" id="top">
      <div className="hero__media">
        <Photo name="hero" priority zoom={false} flush />
      </div>
      <div className="hero__scrim" />

      <div className="hero__body">
        <p className="eyebrow eyebrow--accent reveal is-in">
          Free on the App Store
        </p>
        <h1 className="t-hero hero__title" style={{ marginTop: '1.25rem' }}>
          Every possession counts.
          <br />
          <span className="accent">Now they all get counted.</span>
        </h1>
        <p className="t-lead hero__sub">
          A coach tracks the game from the bench. Every player on the roster
          walks away with a real stat profile, shot charts that show where the
          buckets actually came from, and film attached to the game it came
          from.
        </p>

        <div className="hero__actions">
          <button className="btn btn--primary btn--lg" onClick={onBookDemo}>
            Book a demo
            <ArrowRight size={18} aria-hidden="true" />
          </button>
          <a
            className="btn btn--ghost btn--lg"
            style={{ color: '#fff' }}
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Get it on the App Store
          </a>
        </div>

        <p className="hero__note">
          iPhone. United States. Ages 13 and up. No ads, no in-app purchases.
        </p>
      </div>

      <div
        className="hero__cue"
        style={{ opacity: cueFaded ? 0 : 1 }}
        aria-hidden="true"
      >
        <span className="hero__cue-rail" />
        Scroll
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */

function Marquee() {
  const words = ['Every play', 'Every player', 'One platform'];
  // Two identical tracks so the loop has no seam.
  const track = (
    <div className="marquee__track" aria-hidden="true">
      {[0, 1, 2].map((rep) =>
        words.map((w) => (
          <span className="marquee__item" key={`${rep}-${w}`}>
            {w}
            <span className="marquee__dot" />
          </span>
        )),
      )}
    </div>
  );

  return (
    <div
      className="tile tile--night on-night"
      style={{ paddingBlock: 'clamp(2.5rem, 6vw, 4rem)' }}
    >
      <p className="sr-only">Every play. Every player. One platform.</p>
      <div className="marquee">
        {track}
        {track}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function Problem() {
  return (
    <section className="tile">
      <div className="shell" data-stagger>
        <p className="eyebrow reveal">The problem</p>
        <h2
          className="t-display measure reveal"
          style={{ marginTop: '1.25rem' }}
        >
          A great game disappears by Monday.
        </h2>
        <div className="measure stack reveal" style={{ marginTop: '2rem' }}>
          <p className="t-lead">
            Youth basketball stats live in a paper scorebook, in a parent&rsquo;s
            memory, or nowhere at all. By the time it matters, nobody can prove
            what happened.
          </p>
          <p className="t-body">
            And the numbers a player types into a recruiting page themselves are
            worth almost nothing to the person reading them, because there is no
            way to tell a real 22-point night from a hopeful one. The problem was
            never that kids do not have stats. It is that nobody kept them, and
            nobody could trust the ones that were kept.
          </p>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */

function Tracking() {
  return (
    <section className="tile tile--night on-night" id="tracking">
      <div className="shell">
        <div className="split" data-stagger>
          <div className="stack">
            <p className="eyebrow eyebrow--accent reveal">It starts on the bench</p>
            <h2 className="t-section reveal">
              One coach. One phone. The whole roster&rsquo;s record.
            </h2>
            <p className="t-body reveal">
              The coach already watches every possession. StatMe gives that a
              place to land, and everyone connected to the team gets something
              out of it.
            </p>
            <ul className="checklist reveal">
              <li>
                <Check />
                <span>
                  <strong>Exact stats for every player on the roster.</strong>{' '}
                  Points, rebounds, assists, steals, blocks and shooting
                  percentages, kept for the whole team from one phone.
                </span>
              </li>
              <li>
                <Check />
                <span>
                  <strong>Numbers that carry over.</strong> Season to season and
                  team to team, a player&rsquo;s record follows them instead of
                  starting from zero.
                </span>
              </li>
              <li>
                <Check />
                <span>
                  <strong>Live score and play-by-play.</strong> The game log
                  builds itself while the game is played, and anything can be
                  fixed after the buzzer.
                </span>
              </li>
            </ul>
          </div>

          <div className="reveal reveal--scale">
            <PhoneShot
              name="tracker"
              caption="The live tracker, mid-game. Real screenshot from the app."
            />
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */

function Figures() {
  const [r1, v1] = useCountUp(16);
  const [r2, v2] = useCountUp(13);
  const [r3, v3] = useCountUp(5);

  return (
    <section
      className="tile tile--alt"
      style={{ paddingBlock: 'clamp(3rem, 7vw, 5rem)' }}
    >
      <div className="shell">
        <div className="figures reveal">
          <div className="figure">
            <div className="figure__value" ref={r1}>
              {Math.round(v1)}
            </div>
            <div className="figure__label">Shot chart zones</div>
          </div>
          <div className="figure">
            <div className="figure__value" ref={r2}>
              {Math.round(v2)}
            </div>
            <div className="figure__label">Tracked stat types</div>
          </div>
          <div className="figure">
            <div className="figure__value" ref={r3}>
              {Math.round(v3)} min
            </div>
            <div className="figure__label">Film per clip</div>
          </div>
          <div className="figure">
            <div className="figure__value">$0</div>
            <div className="figure__label">Cost to use</div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */

function Charts() {
  return (
    <section className="tile">
      <div className="shell">
        <div className="split split--flip" data-stagger>
          <div className="stack">
            <p className="eyebrow eyebrow--accent reveal">Shot charts</p>
            <h2 className="t-section reveal">
              Where the buckets actually come from.
            </h2>
            <p className="t-body reveal">
              After the game, every shot sits on the court where it went up
              from. A green O for a make, a red X for a miss. Not a number in
              a table, the actual picture of the night.
            </p>
            <p className="t-body reveal">
              Game by game those charts add up into season hot and cold zones.
              It is the difference between telling a player to shoot better
              and showing them where their points actually come from.
            </p>
          </div>

          <div className="reveal reveal--scale">
            <figure style={{ margin: 0 }}>
              <div className="flat-shot">
                <img
                  src="/img/app/shotchart.jpg"
                  alt="A post-game shot chart from the app: every shot plotted on the court where it was taken, a green O for each make and a red X for each miss."
                  loading="lazy"
                  decoding="async"
                  width="1106"
                  height="1098"
                />
              </div>
              <figcaption className="shot-caption">
                A player&rsquo;s shots from one game, straight from the app.
              </figcaption>
            </figure>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */

function Profile() {
  return (
    <section className="tile tile--alt" id="profile">
      <div className="shell" data-stagger>
        <div style={{ maxWidth: '46rem' }}>
          <p className="eyebrow reveal">The payoff</p>
          <h2 className="t-display reveal" style={{ marginTop: '1.25rem' }}>
            A record that holds up.
          </h2>
          <p className="t-lead reveal" style={{ marginTop: '1.25rem' }}>
            Nobody has to enter their own numbers. The profile is built out of
            what the coach recorded during the game, which is exactly why it is
            worth something to the person reading it.
          </p>
        </div>

        <div className="split split--top" style={{ marginTop: '2.25rem' }}>
          <div className="stack reveal" style={{ paddingTop: '1rem' }}>
            <h3 className="t-section" style={{ fontSize: '1.6rem' }}>
              Per game, per season, per player
            </h3>
            <p className="t-body">
              Box scores for every game, season averages that update
              themselves, and a shot chart that gets more honest the more you
              play. Players see their own profile. Coaches see the whole
              roster. Parents see the player they are linked to.
            </p>
          </div>

          <div className="reveal reveal--scale">
            <PhoneShot
              name="profile"
              caption="A player profile in the app. Real screenshot, example season."
            />
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */

function Film() {
  return (
    <section className="tile tile--night on-night">
      <div className="shell">
        <div className="split" data-stagger>
          <div className="reveal reveal--scale">
            <Photo name="film" />
          </div>

          <div className="stack">
            <p className="eyebrow eyebrow--accent reveal">Film</p>
            <h2 className="t-section reveal">
              Film, attached to the game it came from.
            </h2>
            <p className="t-body reveal">
              Everyone in the gym is already filming. StatMe gives that footage
              a home: a coach uploads a clip and it lives on that game, next to
              the stat line it belongs to. Not a folder of unlabelled videos on
              somebody&rsquo;s phone, and not a link that expires in a group
              chat.
            </p>
            <ul className="checklist reveal">
              <li>
                <Check />
                <span>
                  <strong>Up to five minutes a clip.</strong> Long enough for a
                  run, short enough to actually get watched.
                </span>
              </li>
              <li>
                <Check />
                <span>
                  <strong>Team members only.</strong> Playback runs on signed,
                  expiring links. There is no public URL to pass around.
                </span>
              </li>
              <li>
                <Check />
                <span>
                  <strong>Numbers and footage together.</strong> The stat line
                  says what happened, the film shows how.
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */

function Scouts() {
  return (
    <section className="tile" id="scouts">
      <div className="shell">
        <div className="split split--flip" data-stagger>
          <div className="stack">
            <p className="eyebrow eyebrow--accent reveal">Recruiting</p>
            <h2 className="t-section reveal">
              Scouts read the work, not the highlight reel.
            </h2>
            <p className="t-body reveal">
              College scouts apply and are approved by hand, one at a time.
              Once approved they can find players by class year and position
              and follow the ones they are tracking, working from the same
              recorded stats everybody else sees.
            </p>

          </div>

          <div className="reveal reveal--scale">
            <PhoneShot
              name="boxscore"
              caption="What a scout reads: a finished game with the full box score."
            />
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */

function Roles() {
  const roles = [
    {
      n: '01',
      t: 'Coaches',
      d: 'Record the game from the bench, run the roster and the schedule, upload film, and keep one thread with the parents.',
    },
    {
      n: '02',
      t: 'Players',
      d: 'A profile built from what actually happened, a shot chart that tells you something, and a poster worth posting.',
    },
    {
      n: '03',
      t: 'Parents',
      d: 'Link to your player with a code they send you, then follow their games, their stats, and the team schedule.',
    },
    {
      n: '04',
      t: 'Scouts',
      d: 'Approved by hand. Find players by class and position, follow the ones you are tracking, and talk to their coach.',
    },
  ];

  return (
    <section className="tile tile--night on-night">
      <div className="shell" data-stagger>
        <p className="eyebrow eyebrow--accent reveal">Who it is for</p>
        <h2 className="t-section reveal" style={{ marginTop: '1.25rem' }}>
          Four people, one set of numbers.
        </h2>
        <div className="roles" style={{ marginTop: '2.75rem' }}>
          {roles.map((r) => (
            <article className="role reveal" key={r.n}>
              <span className="role__n">{r.n}</span>
              <h3 className="role__t">{r.t}</h3>
              <p className="role__d">{r.d}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}


/* -------------------------------------------------------------------------- */

function Family() {
  return (
    <section className="tile">
      <div className="shell">
        <div className="split" data-stagger>
          <div className="reveal reveal--scale">
            <PhoneShot
              name="chat"
              caption="The team chat. Any message can be held to report it."
            />
          </div>
          <div className="stack">
            <p className="eyebrow eyebrow--accent reveal">Families</p>
            <h2 className="t-section reveal">
              The drive home gets a lot better.
            </h2>
            <p className="t-body reveal">
              Your player sends you a code and you link straight to them.
              After that the schedule, the games, and the stat lines are just
              there, whether you made it to the gym or not.
            </p>
            <p className="t-body reveal">
              You see your player. Not the whole league, and not anyone
              else&rsquo;s kid.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */

function Close({ onBookDemo }) {
  return (
    <section className="tile close close--photo on-night">
      <div className="close__media" aria-hidden="true">
        <Photo name="gym" zoom={false} flush />
      </div>
      <div className="close__scrim" />
      <div className="shell" data-stagger style={{ position: 'relative' }}>
        <p className="eyebrow eyebrow--accent reveal">Free</p>
        <h2
          className="t-display reveal"
          style={{
            marginTop: '1.25rem',
            maxWidth: '18ch',
            marginInline: 'auto',
          }}
        >
          Time to level up.
        </h2>
        <p
          className="t-lead reveal"
          style={{
            marginTop: '1.5rem',
            maxWidth: '40ch',
            marginInline: 'auto',
            color: 'rgba(234, 240, 246, 0.85)',
          }}
        >
          Track a single game and the profiles build themselves. There is
          nothing to buy and nothing to cancel.
        </p>
        <div
          className="reveal"
          style={{
            marginTop: '2.5rem',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.9rem',
            justifyContent: 'center',
          }}
        >
          <button className="btn btn--primary btn--lg" onClick={onBookDemo}>
            Book a demo
            <ArrowRight size={18} aria-hidden="true" />
          </button>
          <a
            className="btn btn--ghost btn--lg"
            style={{ color: '#fff' }}
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Get it on the App Store
          </a>
        </div>
        <p className="t-small reveal" style={{ marginTop: '1.5rem' }}>
          iPhone. United States. Ages 13 and up.
        </p>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */

function Footer() {
  return (
    <footer className="foot">
      <div className="shell">
        <div className="foot__grid">
          <div>
            <a
              className="nav__brand"
              href="#top"
              style={{ color: 'var(--on-night)' }}
            >
              <img className="nav__mark" src="/number1logo.png" alt="" />
              StatMe
            </a>
            <p style={{ marginTop: '1rem', maxWidth: '32ch' }}>
              Real stats for youth basketball. Recorded by the coach, kept for
              the player.
            </p>
          </div>

          <div>
            <p className="foot__head">Product</p>
            <div className="foot__links">
              <a href="#tracking">Live tracking</a>
              <a href="#profile">Player profile</a>
              <a href="#scouts">Scouts</a>
              <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
                Download on the App Store
              </a>
            </div>
          </div>

          <div>
            <p className="foot__head">Company</p>
            <div className="foot__links">
              <a href="/privacy">Privacy Policy</a>
              <a href="/terms">Terms of Use</a>
              <a href="/support">Support</a>
              <a href="mailto:matthew@statme.us">matthew@statme.us</a>
            </div>
          </div>
        </div>

        <div className="foot__bottom">
          <span>&copy; {new Date().getFullYear()} StatMe LLC</span>
          <span>Made in Texas</span>
        </div>
      </div>
    </footer>
  );
}

/* -------------------------------------------------------------------------- */

export default function App() {
  const root = useRevealRoot();
  const [demoOpen, setDemoOpen] = useState(false);
  const openDemo = () => setDemoOpen(true);

  return (
    <div ref={root}>
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <Nav onBookDemo={openDemo} />
      <main id="main">
        <Hero onBookDemo={openDemo} />
        <Marquee />
        <Problem />
        <Tracking />
        <Figures />
        <Charts />
        <Profile />
        <Film />
        <Scouts />
        <Roles />
        <Family />
        <Close onBookDemo={openDemo} />
      </main>
      <Footer />
      <BookDemo open={demoOpen} onClose={() => setDemoOpen(false)} />
    </div>
  );
}
